import { Auth } from "aws-amplify";
import { useEffect, useState } from "react";
import { CheckCircle, OctagonAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface VerifyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subheader: string;
  attribute: string;
  isCodeSent: boolean;
  onSuccess: () => void;
}

export function VerifyModal({
  open,
  onOpenChange,
  title,
  subheader,
  attribute,
  isCodeSent,
  onSuccess,
}: VerifyModalProps) {
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [closedown, setClosedown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (open && isCodeSent) {
      setCooldown(60);
    }
  }, [open, isCodeSent]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (closedown > 0) {
      const timer = setTimeout(() => {
        setClosedown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (closedown === 0 && showSuccess) {
      setShowSuccess(false);
      onOpenChange(false);
    }
  }, [closedown, showSuccess, onOpenChange]);

  const handleSendCode = async () => {
    setIsSubmitting(true);
    try {
      await Auth.verifyCurrentUserAttribute(attribute);
      setError("");
    } catch (e: any) {
      setError(e.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
      setCode("");
    }
  };

  const handleResend = async () => {
    await handleSendCode();
    setCooldown(60);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await Auth.verifyCurrentUserAttributeSubmit(attribute, code);
      setClosedown(5);
      setCooldown(0);
      setShowSuccess(true);
      onSuccess();
    } catch (e: any) {
      setError(e.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
      setCode("");
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setCode("");
      setError("");
    }
    onOpenChange(nextOpen);
  };

  const isBusy = isSubmitting || closedown > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{subheader}</DialogDescription>
        </DialogHeader>

        {showSuccess && (
          <Alert variant="success">
            <CheckCircle className="size-4" />
            <AlertTitle>Verification successful!</AlertTitle>
          </Alert>
        )}

        {!showSuccess && (
          <div className="space-y-3">
            {error && (
              <Alert variant="destructive">
                <OctagonAlert className="size-4" />
                <AlertTitle>{error}</AlertTitle>
              </Alert>
            )}
            <div className="flex items-center gap-2">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={setCode}
                onComplete={handleSubmit}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              <Button
                disabled={isBusy || code.length !== 6}
                onClick={handleSubmit}
              >
                {isSubmitting && <Spinner />}
                Verify
              </Button>
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleResend}
              disabled={cooldown > 0 || isBusy}
            >
              {cooldown > 0
                ? `Resend in ${cooldown}s`
                : "Send Verification Code"}
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            disabled={isBusy}
            onClick={() => handleOpenChange(false)}
          >
            {closedown > 0 ? `Closing in ${closedown}s` : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
